import { Trans, useTranslation } from 'next-i18next';
import type { SubmitHandler } from 'react-hook-form';
import { EMAIL_REGEX } from '@shared/validation/src/b2c/register';
import { Locale, useLocaleContext } from '@storefront/lib-global';
import { ErrorMessage } from '@storefront/lib-global/baseComponents/errors';
import { useUserContext } from '@storefront/lib-global/context/userContext';
import { useLoadingCursor } from '@storefront/lib-global/hooks/useLoadingCursor';
import { useSizeAndWidthContext } from '../../../context';
import { useEmailMeWhenInStockForm, useNotifyBackInStock } from '../../../hooks';
import { getShortWidthDescription } from '../../../utils';
import {
  BoldText,
  EmailInput,
  SubmitBtn,
  SuccessIcon,
  SuccessMessage,
  SuccessText,
  Text,
  Wrapper,
} from './EmailMeWhenInStockForm.styles';

type EmailMeWhenInStockProps = {
  productName: string;
  error?: boolean;
};

type FormValues = {
  email: string;
};

export function EmailMeWhenInStockForm({ productName, error }: EmailMeWhenInStockProps) {
  const { t } = useTranslation(['lib-products-common']);
  const { selectedSize, selectedWidth } = useSizeAndWidthContext();
  const { customerData } = useUserContext();
  const userEmail = customerData?.email;
  const { handleSubmit, register, validationErrors, isSubmitting, isSubmitSuccessful } =
    useEmailMeWhenInStockForm(userEmail);
  const { notifyBackInStock, data, loading, apiError, setApiError } = useNotifyBackInStock(error);
  const { locale } = useLocaleContext();
  const isNorthAmerica = [Locale.EN_CA, Locale.FR_CA, Locale.EN_US].includes(locale);
  const onSubmit: SubmitHandler<FormValues> = async ({ email }) => {
    await notifyBackInStock(email, productName);
  };

  const handleInputOnChange = () => {
    if (apiError) {
      setApiError(false);
    }
  };

  useLoadingCursor(loading);

  return (
    <Wrapper onSubmit={handleSubmit(onSubmit)}>
      {(!isSubmitSuccessful || !data) && (
        <div data-cs-mask>
          <Trans
            i18nKey="pdp.notifyme.label"
            t={t}
            values={{
              size: selectedSize,
              width: isNorthAmerica ? ` ${selectedWidth}` : getShortWidthDescription(selectedWidth),
            }}
            components={{
              boldText: <BoldText />,
            }}
          />
          <EmailInput
            aria-label={t('pdp.notifyme.email.enter')}
            placeholder={t('pdp.notifyme.email.enter')}
            type="email"
            autoComplete="email"
            maxLength={60}
            hasError={!!validationErrors?.email?.message || apiError}
            {...register('email', {
              required: t('pdp.notifyme.email.valid'),
              pattern: {
                value: EMAIL_REGEX,
                message: t('pdp.notifyme.email.valid'),
              },
              onChange: handleInputOnChange,
            })}
          />
          {validationErrors?.email?.message && (
            <ErrorMessage id="notifyme-email-error">{validationErrors.email.message}</ErrorMessage>
          )}
          {apiError && <ErrorMessage id="notifyme-email-error">{t('pdp.notifyme.email.error')}</ErrorMessage>}
          <SubmitBtn disabled={isSubmitting || loading} type="submit">
            {t('pdp.emailme.cta')}
          </SubmitBtn>
        </div>
      )}
      {isSubmitSuccessful && data && (
        <SuccessMessage>
          <SuccessIcon />
          <SuccessText role="status">
            <Text>
              <Trans
                i18nKey="pdp.notifyme.success.notify"
                t={t}
                values={{
                  size: selectedSize,
                  width: isNorthAmerica ? ` ${selectedWidth}` : getShortWidthDescription(selectedWidth),
                }}
                components={{
                  boldText: <BoldText />,
                }}
              />
            </Text>
          </SuccessText>
        </SuccessMessage>
      )}
    </Wrapper>
  );
}
